"use client";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import Papa from "papaparse";
import EditPOsPanel from "./EditPOsPanel";
import EditWOsPanel from "./EditWOsPanel";
const API = "https://erp-production-72da01c8e651.herokuapp.com/api";

const WO_TYPES = [
  { value: "piecework", label: "Piecework" },
  { value: "hourly", label: "Hourly" },
  { value: "subcontractor", label: "Subcontractor" },
];

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const cls = (...c) => c.filter(Boolean).join(" ");

const safeFloat = (v) => {
  if (v === null || v === undefined || v === "-" || v === "") return 0;
  return parseFloat(String(v).replace(/,/g, "")) || 0;
};

function parseBudgetCSV(rows) {
  const items = [];
  for (const row of rows) {
    const cat = (row["Category"] || "").trim();
    if (cat !== "M" && cat !== "L") continue;
    const sectionNum = (row["Section"] || row["Section #"] || "").trim();
    const bidArea =
      (row["Area"] || row["Bid Area"] || "").trim() || "(unassigned)";
    const matCode = (row["Mat. Code"] || "").trim();
    const laborCode = (row["Labor Code"] || "").trim();
    const itemCode = cat === "M" ? matCode : laborCode;
    const description = (row["Description"] || "").trim();
    const size = (row["Size"] || "").trim();
    const uom = (row["UoM"] || row["UOM"] || "").trim();
    const quantity = safeFloat(row["Quantity"]);
    const reqQty = safeFloat(row["Req Qty"]);
    const laborHours = safeFloat(row["Hours"]);
    const coveragePer = safeFloat(row["Coverage/Unit"]);
    const unitCost = safeFloat(row["Unit Cost"]);
    const amount =
      safeFloat(row["Item Total"]) ||
      safeFloat(row["Mat. Total"]) ||
      safeFloat(row["Labor Total"]);
    const requiredQty = cat === "M" ? reqQty || quantity : 0;
    const unitRate = cat === "L" ? unitCost : 0;
    items.push({
      sectionNumber: sectionNum,
      sectionDescription: sectionNum,
      category: cat,
      costCodeDescription: description,
      itemCode,
      matCode,
      laborCode,
      itemDescription: description,
      size,
      uom,
      container: uom,
      bidArea,
      quantity,
      requiredQty,
      amount,
      laborHours,
      coveragePerUnit: coveragePer || null,
      unitCost: cat === "M" ? unitCost : unitRate,
      price: cat === "M" ? unitCost : unitRate,
      unitRate,
      _groupKey: bidArea,
      _sectionKey: `${bidArea}||${sectionNum}`,
    });
  }
  const poMap = {};
  for (const item of items.filter((i) => i.category === "M")) {
    if (!poMap[item.bidArea]) {
      poMap[item.bidArea] = {
        bidArea: item.bidArea,
        sections: new Set(),
        items: [],
        total: 0,
      };
    }
    poMap[item.bidArea].items.push(item);
    poMap[item.bidArea].sections.add(item.sectionNumber);
    poMap[item.bidArea].total += item.amount;
  }
  for (const g of Object.values(poMap)) g.sections = [...g.sections].sort();
  const woSectionMap = {};
  for (const item of items.filter((i) => i.category === "L")) {
    const key = item._sectionKey;
    if (!woSectionMap[key]) {
      woSectionMap[key] = {
        key,
        bidArea: item.bidArea,
        sectionNumber: item.sectionNumber,
        items: [],
        total: 0,
      };
    }
    woSectionMap[key].items.push(item);
    woSectionMap[key].total += item.amount;
  }
  const totalM = items
    .filter((i) => i.category === "M")
    .reduce((s, i) => s + i.amount, 0);
  const totalL = items
    .filter((i) => i.category === "L")
    .reduce((s, i) => s + i.amount, 0);
  return {
    items,
    poGroups: Object.values(poMap),
    woGroups: Object.values(woSectionMap).sort(
      (a, b) =>
        a.bidArea.localeCompare(b.bidArea) ||
        a.sectionNumber.localeCompare(b.sectionNumber),
    ),
    totals: { totalAmount: totalM + totalL, totalM, totalL },
  };
}

function SummaryCard({ label, value, color = "text-white", sub }) {
  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
        {label}
      </p>
      <p className={`text-xl font-bold ${color}`}>${fmt(value)}</p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function WOTypeSelector({
  woGroups,
  woTypes,
  woGroupNames,
  woUnions,
  unions,
  onTypeChange,
  onGroupChange,
  onUnionChange,
}) {
  const getWOKey = (g) => {
    const groupName = (woGroupNames[g.key] || "").trim();
    if (groupName) return `__group__${groupName}`;
    return `${g.bidArea}||${woTypes[g.key] || "piecework"}`;
  };

  const woMap = {};
  woGroups.forEach((g) => {
    const woKey = getWOKey(g);
    const type = woTypes[g.key] || "piecework";
    const groupName = (woGroupNames[g.key] || "").trim();
    if (!woMap[woKey]) {
      woMap[woKey] = {
        woKey,
        isCrossFloor: !!groupName,
        groupName,
        title: groupName || null,
        type,
        sections: [],
        total: 0,
      };
    }
    woMap[woKey].sections.push(g);
    woMap[woKey].total += g.total;
  });
  const woList = Object.values(woMap);

  const existingGroups = [
    ...new Set(
      Object.values(woGroupNames)
        .map((v) => v.trim())
        .filter(Boolean),
    ),
  ];

  const byFloor = woGroups.reduce((acc, g) => {
    if (!acc[g.bidArea]) acc[g.bidArea] = [];
    acc[g.bidArea].push(g);
    return acc;
  }, {});

  return (
    <div className="space-y-5">
      <div className="bg-blue-900/20 border border-blue-700/40 rounded-lg px-3 py-2.5">
        <p className="text-xs text-blue-300 font-medium mb-0.5">
          Cross-floor grouping
        </p>
        <p className="text-[11px] text-blue-400/80">
          Type a <strong>WO Name</strong> to merge sections across floors into
          one WO. Leave blank for default floor-grouped behaviour.
        </p>
      </div>

      {Object.entries(byFloor).map(([floor, sections]) => (
        <div
          key={floor}
          className="bg-gray-800/60 rounded-xl border border-gray-700 overflow-x-auto"
        >
          <div className="px-3 py-2 bg-gray-900/60 border-b border-gray-700">
            <p className="text-xs font-semibold text-gray-200">{floor}</p>
          </div>
          <div className="grid grid-cols-[1fr_390px_360px_130px] gap-2 px-3 py-1.5 bg-gray-900/30 border-b border-gray-700/50">
            <span className="text-[10px] text-gray-500 uppercase">Section</span>
            <span className="text-[10px] text-gray-500 uppercase">Type</span>
            <span className="text-[10px] text-gray-500 uppercase">Union</span>
            <span className="text-[10px] text-gray-500 uppercase">
              WO Name (optional)
            </span>
          </div>
          <div className="divide-y divide-gray-700/40">
            {sections.map((g) => {
              const currentType = woTypes[g.key] || "piecework";
              const currentUnion = woUnions[g.key] || "non-union";
              const currentGroup = woGroupNames[g.key] || "";
              const isMerged = currentGroup.trim() !== "";
              const isSubcontractor = currentType === "subcontractor";
              const companyUnions = (unions || []).map((u) => ({
                value: u.title,
                label: u.title,
              }));
              // union options: non-union + company unions
              const unionOptions =
                companyUnions.length > 0
                  ? [
                      ...companyUnions,
                      { value: "non-union", label: "Non-Union" },
                    ]
                  : [{ value: "non-union", label: "Non-Union" }];

              const isUnion = currentUnion !== "non-union";

              return (
                <div
                  key={g.key}
                  className={cls(
                    "grid grid-cols-[1fr_140px_300px_250px] gap-2 items-center px-3 py-2",
                    isMerged && "bg-purple-900/10",
                  )}
                >
                  {/* Section label */}
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      {isMerged && (
                        <span className="text-[9px] bg-purple-700/60 text-purple-200 px-1 py-0.5 rounded font-semibold shrink-0">
                          MERGED
                        </span>
                      )}
                      <span className="text-xs text-gray-300 font-mono truncate">
                        {g.sectionNumber || "(unassigned)"}
                      </span>
                    </div>
                    <span className="text-[10px] text-gray-500 font-mono">
                      ${fmt(g.total)}
                    </span>
                  </div>

                  {/* Type selector */}
                  <select
                    value={currentType}
                    onChange={(e) => {
                      const newType = e.target.value;
                      onTypeChange(g.key, newType);
                      // clear union if switching to subcontractor
                      if (newType === "subcontractor")
                        onUnionChange(g.key, "non-union");
                    }}
                    className={cls(
                      "px-2 py-1 text-xs rounded border focus:border-orange-500 focus:outline-none w-full",
                      currentType === "hourly"
                        ? "bg-blue-900/30 border-blue-700/60 text-blue-200"
                        : currentType === "subcontractor"
                          ? "bg-gray-600/40 border-gray-500/60 text-gray-300"
                          : "bg-gray-700 border-gray-600 text-gray-200",
                    )}
                  >
                    {WO_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>

                  {/* Union toggle — disabled for subcontractor */}
                  {isSubcontractor ? (
                    <div className="flex items-center justify-center">
                      <span className="text-[10px] text-gray-600">N/A</span>
                    </div>
                  ) : (
                    <select
                      value={currentUnion}
                      onChange={(e) => onUnionChange(g.key, e.target.value)}
                      className={cls(
                        "px-2 py-1 text-xs rounded border focus:border-orange-500 focus:outline-none w-full",
                        isUnion
                          ? "bg-teal-900/30 border-teal-700/60 text-teal-200"
                          : "bg-gray-700 border-gray-600 text-gray-400",
                      )}
                    >
                      {unionOptions.map((u) => (
                        <option key={u.value} value={u.value}>
                          {u.label}
                        </option>
                      ))}
                    </select>
                  )}

                  {/* WO Name / group merge */}
                  <div className="relative">
                    <input
                      type="text"
                      list="wo-group-suggestions"
                      value={currentGroup}
                      onChange={(e) => onGroupChange(g.key, e.target.value)}
                      placeholder="e.g. Clean Up"
                      className={cls(
                        "w-full px-2 py-1 text-xs rounded border focus:outline-none",
                        isMerged
                          ? "bg-purple-900/20 border-purple-600/60 text-purple-200 placeholder-purple-500 focus:border-purple-400"
                          : "bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-600 focus:border-orange-500",
                      )}
                    />
                    {isMerged && (
                      <button
                        onClick={() => onGroupChange(g.key, "")}
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-purple-400 hover:text-purple-200 text-xs"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <datalist id="wo-group-suggestions">
            {existingGroups.map((g) => (
              <option key={g} value={g} />
            ))}
          </datalist>
        </div>
      ))}

      {/* Will create summary */}
      <div className="bg-gray-900/50 rounded-xl border border-gray-700 overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-700 flex items-center justify-between">
          <p className="text-xs font-semibold text-gray-300">
            Will create {woList.length} Work Order
            {woList.length !== 1 ? "s" : ""}
          </p>
          {woList.filter((w) => w.isCrossFloor).length > 0 && (
            <span className="text-[10px] text-purple-400">
              {woList.filter((w) => w.isCrossFloor).length} cross-floor
            </span>
          )}
        </div>
        <div className="divide-y divide-gray-700/50">
          {woList.map((wo) => {
            const typeLabel =
              WO_TYPES.find((t) => t.value === wo.type)?.label || wo.type;
            const floorNames = [...new Set(wo.sections.map((s) => s.bidArea))];
            const firstSectionUnion =
              woUnions[wo.sections[0]?.key] || "non-union";
            const isUnion = firstSectionUnion !== "non-union";
            const isSubcontractor = wo.type === "subcontractor";
            const displayTitle = wo.isCrossFloor
              ? wo.groupName
              : floorNames[0] === "(unassigned)"
                ? `Unassigned — ${typeLabel}`
                : woList.filter(
                      (w) =>
                        !w.isCrossFloor &&
                        w.sections.some((s) => s.bidArea === floorNames[0]),
                    ).length > 1
                  ? `${floorNames[0]} — ${typeLabel}`
                  : floorNames[0];

            return (
              <div
                key={wo.woKey}
                className={cls(
                  "flex items-center gap-2 px-3 py-2.5",
                  wo.isCrossFloor && "bg-purple-900/10",
                )}
              >
                {/* Type badge */}
                <div
                  className={cls(
                    "shrink-0 px-1.5 py-0.5 text-[9px] font-semibold rounded",
                    wo.isCrossFloor
                      ? "bg-purple-700/60 text-purple-200"
                      : isSubcontractor
                        ? "bg-gray-600/60 text-gray-300"
                        : wo.type === "hourly"
                          ? "bg-blue-800/60 text-blue-200"
                          : "bg-orange-800/60 text-orange-200",
                  )}
                >
                  {wo.isCrossFloor ? "MERGED" : typeLabel.toUpperCase()}
                </div>
                {/* Union badge — only if not subcontractor and is union */}
                {!isSubcontractor && isUnion && (
                  <div className="shrink-0 px-1.5 py-0.5 text-[9px] font-semibold rounded bg-teal-800/60 text-teal-200">
                    {firstSectionUnion}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-200 font-medium truncate">
                    {displayTitle}
                  </p>
                  <p className="text-[10px] text-gray-500">
                    {wo.isCrossFloor
                      ? `${wo.sections.length} sections across ${floorNames.length} floors · ${typeLabel}`
                      : `${wo.sections.length} section${wo.sections.length !== 1 ? "s" : ""}`}
                  </p>
                </div>
                <span className="text-[10px] text-green-400 font-mono shrink-0">
                  ${fmt(wo.total)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function PreviewItemsTable({ items }) {
  const [search, setSearch] = useState("");
  const [sectionFilter, setSectionFilter] = useState("all");

  const sections = useMemo(
    () =>
      [
        "all",
        ...new Set(items.map((i) => i.sectionNumber).filter(Boolean)),
      ].sort(),
    [items],
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter((i) => {
      const matchSection =
        sectionFilter === "all" || i.sectionNumber === sectionFilter;
      const matchQ =
        !q ||
        (i.costCodeDescription + i.itemCode + i.itemDescription + i.size)
          .toLowerCase()
          .includes(q);
      return matchSection && matchQ;
    });
  }, [items, search, sectionFilter]);

  const total = useMemo(
    () => filtered.reduce((s, i) => s + i.amount, 0),
    [filtered],
  );

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search…"
          className="px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none w-44"
        />
        <select
          value={sectionFilter}
          onChange={(e) => setSectionFilter(e.target.value)}
          className="px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-300 focus:border-orange-500 focus:outline-none"
        >
          {sections.map((s) => (
            <option key={s} value={s}>
              {s === "all" ? "All Sections" : `Section ${s}`}
            </option>
          ))}
        </select>
        <span className="text-xs text-gray-500">{filtered.length} items</span>
        <span className="ml-auto text-sm font-semibold text-green-300">
          ${fmt(total)}
        </span>
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-700 max-h-72 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-900 sticky top-0">
            <tr>
              {[
                "Cat",
                "Section",
                "Code",
                "Description",
                "Size",
                "Qty",
                "UOM",
                "Unit Cost",
                "Amount",
              ].map((h) => (
                <th
                  key={h}
                  className="px-3 py-2 text-left text-gray-400 font-medium"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/50">
            {filtered.map((item, i) => (
              <tr
                key={i}
                className={i % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20"}
              >
                <td className="px-3 py-1.5">
                  <span
                    className={cls(
                      "inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded",
                      item.category === "M"
                        ? "bg-blue-900/60 text-blue-300"
                        : "bg-orange-900/60 text-orange-300",
                    )}
                  >
                    {item.category}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-gray-400 font-mono text-[11px]">
                  {item.sectionNumber || "—"}
                </td>
                <td className="px-3 py-1.5 text-gray-400 font-mono">
                  {item.itemCode || "—"}
                </td>
                <td className="px-3 py-1.5 text-gray-200">
                  {item.costCodeDescription || item.itemDescription}
                </td>
                <td className="px-3 py-1.5 text-gray-400">
                  {item.size || "—"}
                </td>
                <td className="px-3 py-1.5 text-right text-gray-300 font-mono">
                  {item.category === "M" ? item.requiredQty : item.quantity}
                </td>
                <td className="px-3 py-1.5 text-center text-gray-400">
                  {item.uom || item.container || "—"}
                </td>
                <td className="px-3 py-1.5 text-right text-gray-300 font-mono">
                  ${fmt(item.unitCost)}
                </td>
                <td
                  className={cls(
                    "px-3 py-1.5 text-right font-mono font-semibold",
                    item.category === "M" ? "text-blue-300" : "text-orange-300",
                  )}
                >
                  ${fmt(item.amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────
export default function BudgetTab3({ project, setProject }) {
  const projectId = project?._id;
  const [budgetDescription, setBudgetDescription] = useState("");
  const [recomputing, setRecomputing] = useState(false);
  const [missingItems, setMissingItems] = useState([]); // rows that couldn't be matched
  const [showMissingReview, setShowMissingReview] = useState(false);
  const [budgetTitle, setBudgetTitle] = useState("");
  const [budgetFile, setBudgetFile] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [unions, setUnions] = useState([]);
  const [budgetItems, setBudgetItems] = useState({});
  const [itemsLoading, setItemsLoading] = useState(false);
  const [actuals, setActuals] = useState(null);
  const [actualsLoading, setActualsLoading] = useState(false);
  // Edit panels
  const [showEditPOs, setShowEditPOs] = useState(false);
  const [showEditWOs, setShowEditWOs] = useState(false);

  const [woTypes, setWoTypes] = useState({});
  const [woGroupNames, setWoGroupNames] = useState({});
  const [woUnions, setWoUnions] = useState({});
  const [parsedData, setParsedData] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [budgetData, setBudgetData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedBudgetId, setExpandedBudgetId] = useState(null);
  const [detailSearch, setDetailSearch] = useState("");
  const [detailCatFilter, setDetailCatFilter] = useState("all");
  const [detailAreaFilter, setDetailAreaFilter] = useState("all");
  const [detailSectionFilter, setDetailSectionFilter] = useState("all");

  const [dialog, setDialog] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });
  const showMsg = (type, title, message) =>
    setDialog({ open: true, type, title, message });
  const closeDialog = () => setDialog((d) => ({ ...d, open: false }));

  useEffect(() => {
    const fetchUnions = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/company-profile/unions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUnions(res.data || []);
      } catch (err) {
        console.error("Failed to fetch unions:", err);
      }
    };
    fetchUnions();
  }, []);

  const loadBudgetData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/budget3/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) setBudgetData(res.data);
    } catch (err) {
      if (err.response?.status !== 404)
        console.error("Budget3 load error:", err.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadBudgetData();
  }, [loadBudgetData]);

  const loadActuals = useCallback(async () => {
    if (!projectId) return;
    setActualsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/budget3/${projectId}/actuals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) setActuals(res.data.actuals);
    } catch (err) {
      console.error("Actuals load error:", err.message);
    } finally {
      setActualsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadActuals();
  }, [loadActuals]);

  const loadBudgetItems = useCallback(
    async (budgetId, filters = {}) => {
      setItemsLoading(true);
      try {
        const token = localStorage.getItem("token");
        const params = new URLSearchParams({ limit: 500, ...filters });
        const res = await axios.get(
          `${API}/budget3/${projectId}/${budgetId}/items?${params}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.data.success)
          setBudgetItems((prev) => ({ ...prev, [budgetId]: res.data.items }));
      } catch (err) {
        console.error("Failed to load budget items:", err.message);
      } finally {
        setItemsLoading(false);
      }
    },
    [projectId],
  );

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBudgetFile(file);
    setParsedData(null);
    setWoTypes({});
    setWoGroupNames({});
    setWoUnions({});
    setMissingItems([]);

    const isXlsx =
      file.name.toLowerCase().endsWith(".xlsx") ||
      file.name.toLowerCase().endsWith(".xls");

    const processRows = (rows) => {
      try {
        const unmatched = rows.filter((row) => {
          const cat = (row["Category"] || "").trim();
          const description = (row["Description"] || "").trim();

          // wrong/missing category
          if (cat !== "M" && cat !== "L") {
            return Object.values(row).some((v) => v !== "");
          }

          // valid category but no description
          if (!description) return true;

          return false;
        });
        setMissingItems(unmatched);

        const data = parseBudgetCSV(rows);
        setParsedData(data);
        const defaults = {};
        const unionDefaults = {};
        data.woGroups.forEach((g) => {
          defaults[g.key] = "piecework";
          unionDefaults[g.key] =
            unions?.length > 0 ? unions[0].title : "non-union";
        });
        setWoTypes(defaults);
        setWoUnions(unionDefaults);
      } catch (err) {
        showMsg("error", "Parse Error", err.message);
      }
    };

    if (isXlsx) {
      // Dynamically import SheetJS (available in the project via npm or CDN)
      import("https://cdn.jsdelivr.net/npm/xlsx@0.18.5/xlsx.mjs")
        .then(({ read, utils }) => {
          const reader = new FileReader();
          reader.onload = (ev) => {
            try {
              const wb = read(ev.target.result, { type: "array" });
              const ws = wb.Sheets[wb.SheetNames[0]];
              const rows = utils.sheet_to_json(ws, { defval: "" });
              processRows(rows);
            } catch (err) {
              showMsg("error", "XLSX Parse Error", err.message);
            }
          };
          reader.readAsArrayBuffer(file);
        })
        .catch(() =>
          showMsg(
            "error",
            "XLSX Support",
            "Could not load XLSX parser. Please save as CSV and retry.",
          ),
        );
    } else {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        encoding: "latin1",
        complete: (result) => processRows(result.data),
        error: (err) => showMsg("error", "File Error", err.message),
      });
    }
  };

  const handleRecompute = async () => {
    setRecomputing(true);
    try {
      const res = await axios.post(
        `${API}/budget3/${projectId}/recompute`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) {
        showMsg("success", "Totals Synced", res.data.message);
        await Promise.all([loadBudgetData(), loadActuals()]);
        // Tell the parent to re-fetch POs and WOs
        if (setProject) setProject((prev) => prev); // triggers handleBudgetUpdated in ProjectView
      }
    } catch (err) {
      showMsg(
        "error",
        "Sync Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setRecomputing(false);
    }
  };

  const handleUpload = async () => {
    if (!budgetTitle.trim() || !parsedData)
      return showMsg(
        "error",
        "Missing Info",
        "Please enter a title and select a valid CSV file.",
      );
    setIsUploading(true);
    try {
      const token = localStorage.getItem("token");
      const lineItems = parsedData.items.map((item) => ({
        sectionNumber: item.sectionNumber,
        sectionDescription: item.sectionNumber,
        category: item.category,

        costCodeDescription: item.costCodeDescription,
        itemCode: item.itemCode,
        matCode: item.matCode,
        laborCode: item.laborCode,
        itemDescription: item.itemDescription,
        size: item.size,
        bidArea: item.bidArea,
        uom: item.uom,
        container: item.uom,
        coveragePerUnit: item.coveragePerUnit,
        amount: item.amount,
        requiredQty: item.requiredQty,
        unitCost: item.unitCost,
        price: item.price,
        quantity: item.quantity,
        laborHours: item.laborHours,
        unitRate: item.unitRate,
      }));
      const res = await axios.post(
        `${API}/budget3/${projectId}`,
        { title: budgetTitle, lineItems, woTypes, woGroupNames, woUnions },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) {
        showMsg(
          "success",
          "Budget Created",
          `${res.data.summary.itemCount} items uploaded.\n${res.data.summary.poCount} POs and ${res.data.summary.woCount} WOs generated.\nTotal: $${fmt(res.data.summary.grandTotal)}`,
        );
        setBudgetTitle("");
        setBudgetFile(null);
        setParsedData(null);
        setMissingItems([]);
        setWoTypes({});
        setWoGroupNames({});
        setWoUnions({});
        setShowUploadForm(false);
        await loadBudgetData();
      }
    } catch (err) {
      showMsg(
        "error",
        "Upload Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setIsUploading(false);
    }
  };

  const expandedBudget = useMemo(
    () => budgetData?.budgets?.find((b) => b._id === expandedBudgetId),
    [budgetData, expandedBudgetId],
  );
  const detailItems = budgetItems[expandedBudgetId] || [];
  const uniqueAreas = useMemo(
    () => [
      "all",
      ...new Set(detailItems.map((i) => i.bidArea).filter(Boolean)),
    ],
    [detailItems],
  );
  const uniqueSections = useMemo(() => {
    const base = detailItems.filter(
      (i) => detailAreaFilter === "all" || i.bidArea === detailAreaFilter,
    );
    return [
      "all",
      ...new Set(base.map((i) => i.sectionNumber).filter(Boolean)),
    ].sort();
  }, [detailItems, detailAreaFilter]);
  const handleAreaFilterChange = (val) => {
    setDetailAreaFilter(val);
    setDetailSectionFilter("all");
  };
  const filteredItems = useMemo(() => {
    const q = detailSearch.toLowerCase();
    return detailItems.filter((item) => {
      const matchCat =
        detailCatFilter === "all" || item.category === detailCatFilter;
      const matchArea =
        detailAreaFilter === "all" || item.bidArea === detailAreaFilter;
      const matchSection =
        detailSectionFilter === "all" ||
        item.sectionNumber === detailSectionFilter;
      const matchQ =
        !q ||
        (item.costCodeDescription || "").toLowerCase().includes(q) ||
        (item.itemCode || "").toLowerCase().includes(q) ||
        (item.size || "").toLowerCase().includes(q);
      return matchCat && matchArea && matchSection && matchQ;
    });
  }, [
    detailItems,
    detailSearch,
    detailCatFilter,
    detailAreaFilter,
    detailSectionFilter,
  ]);
  const detailTotals = useMemo(
    () =>
      filteredItems.reduce(
        (acc, i) => ({
          total: acc.total + (i.amount || 0),
          m: acc.m + (i.category === "M" ? i.amount || 0 : 0),
          l: acc.l + (i.category === "L" ? i.amount || 0 : 0),
        }),
        { total: 0, m: 0, l: 0 },
      ),
    [filteredItems],
  );

  const token = localStorage.getItem("token");
  const hasBudget = !!budgetData?.budgets?.length;
  const summary = budgetData?.summary || {};

  return (
    <div className="space-y-6">
      {/* Modals */}
      {showEditPOs && (
        <EditPOsPanel
          projectId={projectId}
          token={token}
          onClose={() => setShowEditPOs(false)}
          onSaved={() => {
            loadBudgetData();
            loadActuals();
          }}
        />
      )}
      {showEditWOs && (
        <EditWOsPanel
          projectId={projectId}
          token={token}
          unions={unions}
          onClose={() => setShowEditWOs(false)}
          onSaved={() => {
            loadBudgetData();
            loadActuals();
          }}
        />
      )}

      {/* Dialog */}
      {dialog.open && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60">
          <div className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <h3
              className={cls(
                "text-lg font-semibold mb-2",
                dialog.type === "error"
                  ? "text-red-400"
                  : dialog.type === "success"
                    ? "text-green-400"
                    : "text-orange-400",
              )}
            >
              {dialog.title}
            </h3>
            <p className="text-gray-300 text-sm whitespace-pre-line">
              {dialog.message}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                onClick={closeDialog}
                className="px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showMissingReview && missingItems.length > 0 && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60">
          <div className="bg-gray-800 border border-yellow-600/50 rounded-xl shadow-2xl max-w-3xl w-full mx-4 p-6 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-base font-semibold text-yellow-400">
                  ⚠ {missingItems.length} unmatched row
                  {missingItems.length !== 1 ? "s" : ""}
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  These rows were skipped because their{" "}
                  <strong className="text-gray-200">Category</strong> column is
                  not <code className="text-yellow-300">M</code> or{" "}
                  <code className="text-yellow-300">L</code>. Review them below
                  — they will <em>not</em> be uploaded.
                </p>
              </div>
              <button
                onClick={() => setShowMissingReview(false)}
                className="text-gray-400 hover:text-white text-2xl font-bold w-8 h-8 flex items-center justify-center"
              >
                ×
              </button>
            </div>

            <div className="overflow-auto flex-1 rounded-lg border border-gray-700">
              <table className="w-full text-xs">
                <thead className="bg-gray-900 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-gray-400 font-medium">
                      #
                    </th>
                    {Object.keys(missingItems[0] || {})
                      .slice(0, 8)
                      .map((k) => (
                        <th
                          key={k}
                          className="px-3 py-2 text-left text-gray-400 font-medium truncate max-w-[120px]"
                        >
                          {k}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/50">
                  {missingItems.map((row, i) => (
                    <tr
                      key={i}
                      className={
                        i % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20"
                      }
                    >
                      <td className="px-3 py-1.5 text-gray-500">{i + 1}</td>
                      {Object.values(row)
                        .slice(0, 8)
                        .map((v, j) => (
                          <td
                            key={j}
                            className="px-3 py-1.5 text-gray-300 truncate max-w-[120px]"
                          >
                            {String(v) || "—"}
                          </td>
                        ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setShowMissingReview(false)}
                className="px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-lg hover:bg-yellow-700"
              >
                Understood, continue
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedGroup && (
        <div className="fixed inset-0 z-[99998] flex items-center justify-center bg-black/60">
          <div className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl w-full max-w-4xl mx-4 p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-semibold text-white">
                  {selectedGroup.type === "PO"
                    ? "Purchase Order"
                    : "Work Order"}{" "}
                  Preview
                </h3>
                <p className="text-sm text-gray-400">
                  Floor:{" "}
                  <span className="text-white">
                    {selectedGroup.group.bidArea}
                  </span>
                  <span className="mx-2 text-gray-600">·</span>
                  {selectedGroup.type === "PO"
                    ? `${selectedGroup.group.sections?.length ?? 0} section${(selectedGroup.group.sections?.length ?? 0) !== 1 ? "s" : ""}`
                    : `Section ${selectedGroup.group.sectionNumber || "(unassigned)"}`}
                  <span className="ml-2 text-green-300 font-semibold">
                    ${fmt(selectedGroup.group.total)}
                  </span>
                </p>
              </div>
              <button
                onClick={() => setSelectedGroup(null)}
                className="text-gray-400 hover:text-white text-2xl font-bold w-8 h-8 flex items-center justify-center"
              >
                ×
              </button>
            </div>
            <PreviewItemsTable items={selectedGroup.group.items || []} />
          </div>
        </div>
      )}

      {/* Summary cards */}
      {hasBudget && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <SummaryCard
            label="Original Estimated"
            value={summary.originalEstimated}
            color="text-white"
          />
          <SummaryCard
            label="Change Orders"
            value={summary.changeOrderAmount}
            color="text-purple-300"
          />
          <SummaryCard
            label="Running Total"
            value={summary.runningTotal}
            color="text-orange-300"
          />
          <SummaryCard
            label="Materials Delivered"
            value={actuals?.materialDelivered ?? 0}
            color="text-blue-300"
            sub={
              actualsLoading
                ? "Loading…"
                : `${actuals?.materialQtyDelivered ?? 0} units · $${fmt(actuals?.materialOrdered ?? 0)} ordered`
            }
          />
          <SummaryCard
            label="Labour Actual"
            value={
              (actuals?.labourApprovedCombined ?? 0) +
              (actuals?.labourPendingCombined ?? 0)
            }
            color="text-orange-300"
            sub={
              actualsLoading
                ? "Loading…"
                : `$${fmt(actuals?.labourApprovedCombined ?? 0)} approved · $${fmt(actuals?.labourPendingCombined ?? 0)} pending`
            }
          />
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
              Total Actual
            </p>
            {actualsLoading ? (
              <div className="animate-pulse h-6 bg-gray-700 rounded w-24 mt-1" />
            ) : (
              <>
                <p className="text-xl font-bold text-green-300">
                  ${fmt(actuals?.totalActual ?? 0)}
                </p>
                <p className="text-xs mt-0.5">
                  {actuals && summary.runningTotal > 0 ? (
                    <span className="text-gray-400">
                      {(
                        (actuals.totalActual / summary.runningTotal) *
                        100
                      ).toFixed(1)}
                      % of ${fmt(summary.runningTotal)}
                    </span>
                  ) : null}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!hasBudget && !showUploadForm && !loading && (
        <div className="bg-gray-800 rounded-xl border border-dashed border-gray-600 p-10 text-center">
          <div className="mx-auto w-12 h-12 bg-orange-500/10 rounded-xl flex items-center justify-center mb-4">
            <svg
              className="w-6 h-6 text-orange-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-white mb-1">
            No Budget Yet
          </h3>
          <p className="text-sm text-gray-400 mb-6 max-w-sm mx-auto">
            Upload your budget CSV. One PO (material) and one WO (labour) will
            be created per floor, with sections as sub-filters.
          </p>
          <button
            onClick={() => setShowUploadForm(true)}
            className="px-5 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors"
          >
            Upload Budget
          </button>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
          <span className="ml-3 text-gray-400 text-sm">Loading…</span>
        </div>
      )}

      {/* Upload form */}
      {showUploadForm && !hasBudget && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 space-y-5">
          <h3 className="text-base font-semibold text-white">Upload Budget</h3>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">
              Budget Title *
            </label>
            <input
              type="text"
              value={budgetTitle}
              onChange={(e) => setBudgetTitle(e.target.value)}
              placeholder="e.g. 800 Yonge — Base Budget"
              className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">
              CSV File *
            </label>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-orange-500 file:text-white hover:file:bg-orange-600"
            />
            <p className="text-xs text-gray-500 mt-1.5">
              Each floor = 1 PO + 1 WO. Rearrange after upload with Edit POs /
              Edit WOs.
            </p>
          </div>

          {parsedData && (
            <div className="bg-gray-700/50 rounded-xl p-4 space-y-4">
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <span className="text-gray-300">
                  <span className="font-semibold text-white">
                    {parsedData.items.length}
                  </span>{" "}
                  items parsed
                </span>
                <span className="text-blue-300">
                  M: ${fmt(parsedData.totals.totalM)}
                </span>
                <span className="text-orange-300">
                  L: ${fmt(parsedData.totals.totalL)}
                </span>
                <span className="text-green-300 font-semibold">
                  Total: ${fmt(parsedData.totals.totalAmount)}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                <div>
                  <p className="text-gray-400 mb-2 font-medium">
                    Will generate{" "}
                    <span className="text-blue-300 font-semibold">
                      {parsedData.poGroups.length} Purchase Orders
                    </span>{" "}
                    <span className="text-gray-500">(1 per floor)</span>
                  </p>
                  <div className="space-y-0.5 max-h-40 overflow-y-auto pr-1">
                    {parsedData.poGroups.map((g, i) => (
                      <div
                        key={i}
                        onClick={() =>
                          setSelectedGroup({ type: "PO", group: g })
                        }
                        className="flex items-center justify-between hover:bg-gray-600/50 rounded px-2 py-1 cursor-pointer group"
                      >
                        <div className="flex-1 min-w-0 mr-2">
                          <span className="text-gray-300 truncate group-hover:text-white block">
                            {g.bidArea}
                          </span>
                          <span className="text-gray-500 text-[10px]">
                            {g.sections.length} section
                            {g.sections.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <span className="text-green-300 shrink-0">
                          ${fmt(g.total)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-gray-400 mb-2 font-medium">
                    Will generate{" "}
                    <span className="text-purple-300 font-semibold">
                      {
                        Object.keys(
                          (parsedData.woGroups || []).reduce((acc, g) => {
                            const t = (g.key && woTypes[g.key]) || "piecework";
                            acc[`${g.bidArea}||${t}`] = true;
                            return acc;
                          }, {}),
                        ).length
                      }{" "}
                      Work Orders
                    </span>{" "}
                    <span className="text-gray-500">(by floor + type)</span>
                  </p>
                  <div className="space-y-0.5 max-h-40 overflow-y-auto pr-1">
                    {Object.values(
                      (parsedData.woGroups || []).reduce((acc, g) => {
                        const t = (g.key && woTypes[g.key]) || "piecework";
                        const woKey = `${g.bidArea}||${t}`;
                        if (!acc[woKey])
                          acc[woKey] = {
                            bidArea: g.bidArea,
                            woType: t,
                            sectionGroups: [],
                            total: 0,
                            items: [],
                          };
                        acc[woKey].sectionGroups.push(g);
                        acc[woKey].total += g.total;
                        acc[woKey].items = [
                          ...acc[woKey].items,
                          ...(g.items || []),
                        ];
                        return acc;
                      }, {}),
                    ).map((merged, i) => {
                      const typeLabel =
                        WO_TYPES.find(
                          (t) => t.value === (merged.woType || "piecework"),
                        )?.label || "Piecework";
                      return (
                        <div
                          key={i}
                          onClick={() =>
                            setSelectedGroup({
                              type: "WO",
                              group: { ...merged, sectionNumber: null },
                            })
                          }
                          className="flex items-center justify-between hover:bg-gray-600/50 rounded px-2 py-1 cursor-pointer group"
                        >
                          <div className="flex-1 min-w-0 mr-2">
                            <span className="text-gray-300 truncate group-hover:text-white block">
                              {merged.bidArea}
                              <span className="ml-1 text-[10px] text-purple-400">
                                — {typeLabel}
                              </span>
                            </span>
                            <span className="text-gray-500 text-[10px]">
                              {merged.sectionGroups.length} section
                              {merged.sectionGroups.length !== 1 ? "s" : ""}
                            </span>
                          </div>
                          <span className="text-green-300 shrink-0">
                            ${fmt(merged.total)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              {parsedData.woGroups.length > 0 && (
                <div className="border-t border-gray-600 pt-4">
                  <WOTypeSelector
                    woGroups={parsedData.woGroups}
                    woTypes={woTypes}
                    woGroupNames={woGroupNames}
                    woUnions={woUnions}
                    unions={unions}
                    onTypeChange={(key, type) =>
                      setWoTypes((prev) => ({ ...prev, [key]: type }))
                    }
                    onGroupChange={(key, groupName) =>
                      setWoGroupNames((prev) => ({ ...prev, [key]: groupName }))
                    }
                    onUnionChange={(key, union) =>
                      setWoUnions((prev) => ({ ...prev, [key]: union }))
                    }
                  />
                </div>
              )}
            </div>
          )}

          {missingItems.length > 0 && (
            <div className="rounded-lg border border-yellow-600/50 bg-yellow-900/10 p-4 space-y-2">
              <p className="text-xs font-semibold text-yellow-400">
                ⚠ {missingItems.length} row
                {missingItems.length !== 1 ? "s" : ""} cannot be imported — fix
                your file before uploading
              </p>
              <p className="text-xs text-yellow-600">
                The <code className="text-yellow-400">Category</code> column
                must be <code className="text-yellow-400">M</code> (material) or{" "}
                <code className="text-yellow-400">L</code> (labour) for every
                non-empty row.
              </p>
              <div className="overflow-x-auto rounded border border-yellow-700/40 max-h-48 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-900 sticky top-0">
                    <tr>
                      <th className="px-3 py-1.5 text-left text-gray-400 font-medium">
                        #
                      </th>
                      {Object.keys(missingItems[0] || {})
                        .slice(0, 7)
                        .map((k) => (
                          <th
                            key={k}
                            className="px-3 py-1.5 text-left text-yellow-600 font-medium truncate max-w-[100px]"
                          >
                            {k}
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/50">
                    {missingItems.map((row, i) => (
                      <tr key={i} className="bg-yellow-900/10">
                        <td className="px-3 py-1.5 text-gray-500">{i + 1}</td>
                        {Object.values(row)
                          .slice(0, 7)
                          .map((v, j) => (
                            <td
                              key={j}
                              className="px-3 py-1.5 text-yellow-200 truncate max-w-[100px]"
                            >
                              {String(v) || "—"}
                            </td>
                          ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-gray-500">
                Fix the highlighted rows in your spreadsheet, then re-upload the
                file.
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button
              onClick={handleUpload}
              disabled={
                !budgetTitle.trim() ||
                !parsedData ||
                isUploading ||
                missingItems.length > 0
              }
              className={cls(
                "px-5 py-2.5 text-sm font-semibold rounded-lg text-white transition-colors",
                !budgetTitle.trim() || !parsedData || isUploading
                  ? "bg-gray-600 cursor-not-allowed"
                  : "bg-orange-500 hover:bg-orange-600",
              )}
            >
              {isUploading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin inline-block w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full" />
                  Uploading…
                </span>
              ) : (
                "Upload & Generate POs / WOs"
              )}
            </button>
            <button
              onClick={() => {
                setShowUploadForm(false);
                setParsedData(null);
                setBudgetFile(null);
                setBudgetTitle("");
                setWoTypes({});
                setWoGroupNames({});
                setWoUnions({});
              }}
              className="px-4 py-2 text-sm text-gray-400 hover:text-white rounded-lg border border-gray-600 hover:border-gray-400 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Budget list */}
      {hasBudget && (
        <div className="space-y-3">
          {/* Header with Edit POs / Edit WOs buttons */}
          <div className="flex items-center gap-2">
            {/* <button
              onClick={handleRecompute}
              disabled={recomputing}
              className="px-3 py-1.5 text-xs font-semibold text-white bg-green-900 border border-green-700/80 rounded-lg hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              {recomputing ? (
                <>
                  <span className="animate-spin inline-block w-3 h-3 border-2 border-white/30 border-t-white rounded-full" />
                  Syncing…
                </>
              ) : (
                "↺ Sync Totals"
              )}
            </button> */}
            <button
              onClick={() => setShowEditPOs(true)}
              className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-900 border border-blue-700/80 rounded-lg hover:bg-blue-900/50 transition-colors"
            >
              Edit POs
            </button>
            <button
              onClick={() => setShowEditWOs(true)}
              className="px-3 py-1.5 text-xs font-semibold text-white bg-orange-900 border border-orange-700/80 rounded-lg hover:bg-orange-900/50 transition-colors"
            >
              Edit WOs
            </button>
          </div>

          {(budgetData.budgets || []).map((budget) => {
            const isExpanded = expandedBudgetId === budget._id;
            const budgetTotal = budget.grandTotal || 0;
            return (
              <div
                key={budget._id}
                className="rounded-xl border border-gray-600 shadow-md overflow-hidden bg-gray-800"
              >
                <div className="flex items-center justify-between px-5 py-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-semibold text-white">
                        {budget.title}
                      </h4>
                      <span
                        className={cls(
                          "px-1.5 py-0.5 text-[10px] font-semibold rounded",
                          budget.status === "confirmed"
                            ? "bg-green-600/70 text-green-100"
                            : "bg-yellow-600/70 text-yellow-100",
                        )}
                      >
                        {budget.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {budget.itemCount || 0} items ·{" "}
                      <span className="text-blue-300">
                        M: ${fmt(budget.totalMaterial)}
                      </span>{" "}
                      <span className="text-orange-300">
                        L: ${fmt(budget.totalLabor)}
                      </span>
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-green-300">
                      ${fmt(budgetTotal)}
                    </span>
                    <button
                      onClick={() => {
                        const next = isExpanded ? null : budget._id;
                        setExpandedBudgetId(next);
                        setDetailSearch("");
                        setDetailCatFilter("all");
                        setDetailAreaFilter("all");
                        setDetailSectionFilter("all");
                        if (next) loadBudgetItems(next);
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      {isExpanded ? "Collapse" : "Expand"}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-gray-700 px-5 pb-5 pt-4">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <input
                        type="text"
                        value={detailSearch}
                        onChange={(e) => setDetailSearch(e.target.value)}
                        placeholder="Search items…"
                        className="px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none w-44"
                      />
                      <div className="flex rounded overflow-hidden border border-gray-600">
                        {["all", "M", "L"].map((c) => (
                          <button
                            key={c}
                            onClick={() => setDetailCatFilter(c)}
                            className={cls(
                              "px-2 py-1 text-xs font-medium transition-colors",
                              detailCatFilter === c
                                ? "bg-orange-500 text-white"
                                : "bg-gray-700 text-gray-400 hover:bg-gray-600",
                            )}
                          >
                            {c === "all" ? "All" : c}
                          </button>
                        ))}
                      </div>
                      <select
                        value={detailAreaFilter}
                        onChange={(e) => handleAreaFilterChange(e.target.value)}
                        className="px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-300 focus:border-orange-500 focus:outline-none"
                      >
                        {uniqueAreas.map((a) => (
                          <option key={a} value={a}>
                            {a === "all" ? "All Floors" : a}
                          </option>
                        ))}
                      </select>
                      {detailAreaFilter !== "all" && (
                        <select
                          value={detailSectionFilter}
                          onChange={(e) =>
                            setDetailSectionFilter(e.target.value)
                          }
                          className="px-2 py-1 text-xs bg-gray-700 border border-blue-700/50 rounded text-blue-300 focus:border-orange-500 focus:outline-none"
                        >
                          {uniqueSections.map((s) => (
                            <option key={s} value={s}>
                              {s === "all" ? "All Sections" : `Section ${s}`}
                            </option>
                          ))}
                        </select>
                      )}
                      <div className="ml-auto flex items-center gap-3 text-xs">
                        <span className="text-white font-semibold">
                          {filteredItems.length}
                        </span>
                        {filteredItems.length !== detailItems.length && (
                          <span className="text-gray-500">
                            / {detailItems.length}
                          </span>
                        )}
                        <span className="text-gray-400">items</span>
                        <span className="text-blue-300">
                          M: ${fmt(detailTotals.m)}
                        </span>
                        <span className="text-orange-300">
                          L: ${fmt(detailTotals.l)}
                        </span>
                        <span className="text-green-300 font-semibold">
                          Total: ${fmt(detailTotals.total)}
                        </span>
                      </div>
                    </div>

                    {itemsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
                        <span className="ml-2 text-gray-400 text-sm">
                          Loading items…
                        </span>
                      </div>
                    ) : (
                      <div className="overflow-x-auto rounded-lg border border-gray-700 max-h-[480px] overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-900 sticky top-0 z-10">
                            <tr>
                              {[
                                "Cat",
                                "Floor",
                                "Section",
                                "Code",
                                "Description",
                                "Size",
                                "Qty",
                                "UOM",
                                "Unit Cost",
                                "Amount",
                              ].map((h) => (
                                <th
                                  key={h}
                                  className="px-3 py-2 text-left text-gray-400 font-medium"
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-700/50">
                            {filteredItems.map((item, idx) => {
                              const displayQty =
                                item.category === "M"
                                  ? (item.requiredQty ?? item.quantity ?? 0)
                                  : (item.quantity ?? 0);
                              const displayCost =
                                item.category === "M"
                                  ? (item.unitCost ?? item.price ?? 0)
                                  : (item.unitRate ?? item.price ?? 0);
                              return (
                                <tr
                                  key={item._id || idx}
                                  className={cls(
                                    idx % 2 === 0
                                      ? "bg-gray-800/40"
                                      : "bg-gray-700/20",
                                    "hover:bg-gray-600/30",
                                  )}
                                >
                                  <td className="px-3 py-1.5">
                                    <span
                                      className={cls(
                                        "inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded",
                                        item.category === "M"
                                          ? "bg-blue-900/60 text-blue-300"
                                          : "bg-orange-900/60 text-orange-300",
                                      )}
                                    >
                                      {item.category}
                                    </span>
                                  </td>
                                  <td className="px-3 py-1.5 text-gray-400 text-[11px]">
                                    {item.bidArea || "—"}
                                  </td>
                                  <td className="px-3 py-1.5 text-gray-400 font-mono text-[11px]">
                                    {item.sectionNumber || "—"}
                                  </td>
                                  <td className="px-3 py-1.5 text-gray-400 font-mono">
                                    {item.itemCode || "—"}
                                  </td>
                                  <td className="px-3 py-1.5 text-gray-200">
                                    {item.costCodeDescription ||
                                      item.itemDescription ||
                                      "—"}
                                  </td>
                                  <td className="px-3 py-1.5 text-gray-400">
                                    {item.size || "—"}
                                  </td>
                                  <td className="px-3 py-1.5 text-right text-gray-300 font-mono">
                                    {displayQty}
                                  </td>
                                  <td className="px-3 py-1.5 text-center text-gray-400">
                                    {item.uom || item.container || "—"}
                                  </td>
                                  <td className="px-3 py-1.5 text-right text-gray-300 font-mono">
                                    ${fmt(displayCost)}
                                  </td>
                                  <td
                                    className={cls(
                                      "px-3 py-1.5 text-right font-mono font-semibold",
                                      item.category === "M"
                                        ? "text-blue-300"
                                        : "text-orange-300",
                                    )}
                                  >
                                    ${fmt(item.amount || 0)}
                                  </td>
                                </tr>
                              );
                            })}
                            {filteredItems.length === 0 && (
                              <tr>
                                <td
                                  colSpan={10}
                                  className="px-3 py-8 text-center text-gray-500"
                                >
                                  No items match your filters.
                                </td>
                              </tr>
                            )}
                          </tbody>
                          <tfoot className="bg-gray-900 sticky bottom-0 border-t border-gray-600">
                            <tr>
                              <td
                                colSpan={9}
                                className="px-3 py-2 text-xs text-gray-400"
                              >
                                {filteredItems.length} items shown
                              </td>
                              <td className="px-3 py-2 text-right text-sm font-bold text-green-300 font-mono">
                                ${fmt(detailTotals.total)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* PO / WO quick list */}
          {(budgetData.purchaseOrders?.length > 0 ||
            budgetData.workOrders?.length > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              {budgetData.purchaseOrders?.length > 0 && (
                <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
                  <div className="bg-gray-900 px-4 py-2.5 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-blue-300">
                      Purchase Orders ({budgetData.purchaseOrders.length})
                    </h4>
                    <button
                      onClick={() => setShowEditPOs(true)}
                      className="text-xs text-blue-400 hover:text-blue-300"
                    >
                      Edit →
                    </button>
                  </div>
                  <div className="divide-y divide-gray-700/50 max-h-64 overflow-y-auto">
                    {(budgetData.purchaseOrders || []).map((po) => (
                      <div
                        key={po._id}
                        className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-700/40 transition-colors"
                      >
                        <div>
                          <p className="text-xs font-medium text-gray-200">
                            {po.title}
                          </p>
                          <p className="text-[11px] text-gray-500">
                            PO #{po.poNumber}
                            {po.sectionCount > 0
                              ? ` · ${po.sectionCount} sections`
                              : ""}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-semibold text-green-300">
                            ${fmt(po.originalEstimatedAmount)}
                          </p>
                          <span
                            className={cls(
                              "text-[10px] px-1.5 py-0.5 rounded font-medium",
                              po.status === "active"
                                ? "bg-green-900/50 text-green-300"
                                : "bg-gray-700 text-gray-400",
                            )}
                          >
                            {po.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {budgetData.workOrders?.length > 0 && (
                <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
                  <div className="bg-gray-900 px-4 py-2.5 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-purple-300">
                      Work Orders ({budgetData.workOrders.length})
                    </h4>
                    <button
                      onClick={() => setShowEditWOs(true)}
                      className="text-xs text-orange-400 hover:text-orange-300"
                    >
                      Edit →
                    </button>
                  </div>
                  <div className="divide-y divide-gray-700/50 max-h-64 overflow-y-auto">
                    {(budgetData.workOrders || []).map((wo) => (
                      <div
                        key={wo._id}
                        className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-700/40 transition-colors"
                      >
                        <div>
                          <p className="text-xs font-medium text-gray-200">
                            {wo.title}
                          </p>
                          <p className="text-[11px] text-gray-500">
                            {wo.woNumber}
                            {wo.type && (
                              <span className="ml-1.5 px-1 py-0.5 bg-gray-700 rounded text-gray-400 text-[9px] uppercase">
                                {wo.type.replace("_", " ")}
                              </span>
                            )}
                            {wo.union && wo.union !== "non-union" && (
                              <span className="ml-1 text-[9px] text-teal-400">
                                {wo.union}
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-semibold text-green-300">
                            ${fmt(wo.originalEstimatedAmount)}
                          </p>
                          <span
                            className={cls(
                              "text-[10px] px-1.5 py-0.5 rounded font-medium",
                              wo.status === "active"
                                ? "bg-green-900/50 text-green-300"
                                : "bg-gray-700 text-gray-400",
                            )}
                          >
                            {wo.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
